import { Title } from '@components/atoms/Title';
import { GardenAccordion, LoadingHome, ModalContainer } from '@components/molecules';
import { SkeletonRow } from '@components/organism/SkeletonRow';
import { urlZaf } from '@services/Zendesk';
import { GoPencil, GoTrash } from 'react-icons/go';
import { useNavigate } from 'react-router-dom';
import { useHome } from './useHome';
import { formatCNPJ, formateCpf, formatPhone, formatRole } from '@hooks/masks';
import { Button, Footer } from '@components/atoms';
import { MdOutlineHistory } from 'react-icons/md';
import { IoIosAdd } from 'react-icons/io';
import type { User } from "types/UserZendesk";

export function Home() {
    const navigate = useNavigate();
    const { problemMessage, loadingCreateObject, usersInfo, loading, setSelectedUser, visible, onClose, openModal, handleUnlinkUser, userToDelete, availableRoles, setSelectedRole, trueRoles } = useHome();

    if (loadingCreateObject || loading) {
        return <LoadingHome />
    }
    if(problemMessage) {
        return (
            <div className="flex align-center justify-center mt-10 text-red-600 text-center">
                <p>Houve um problema ao carregar as contas vinculadas ao ticket<br /> {problemMessage}</p>
            </div>
        )
    }

    return (
        <>
            {!!visible && (
                <ModalContainer
                    onClose={onClose}
                    sendCancel={() => userToDelete && handleUnlinkUser(userToDelete)}
                    title="Tem certeza?"
                    isDanger={true}
                    content={
                        <p>
                            Tem certeza de que deseja remover o usuário desta <strong>conta</strong>?
                        </p>
                    }
                />
            )}
            <div className="px-4 flex flex-col gap-4 max-h-[calc(100vh-200px)] overflow-y-auto mb-15">
                <Title subtitle="Contas vinculadas ao ticket:" />
                
                <div className="flex flex-col gap-4 mb-10">
                    {usersInfo.map((user: User, key) => (
                        <GardenAccordion
                            key={key}
                            items={{
                                title: formatRole(user.role.charAt(0).toUpperCase() + user.role.slice(1)),
                                content: (
                                    <div className="min-h-[200px]">

                                        {[
                                            {
                                                label: `${user.user_fields?.cnpj_regex === "" ? 'Razão Social' : 'Nome'}`,
                                                value: (
                                                    <a
                                                        href={`${urlZaf}/agent/users/${user.id}/requested_tickets`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 underline"
                                                    >
                                                        {user.name || user.user_fields?._pessoa_juridica__razao_social}
                                                    </a>
                                                )
                                            },
                                            { label: 'Documento', value: formateCpf(user.user_fields?.cpf_3 ?? '-') || formatCNPJ(user.user_fields?.cnpj_regex || '-') || '—' },
                                            { label: 'Número', value: formatPhone(user.phone || '') ?? '—' },
                                            { label: 'E-mail', value: user.email ?? '—' },
                                            { label: 'CEP', value: user.user_fields?.cep__endereco ?? '—' },
                                            { label: 'Endereço', value: user.user_fields?.endereco_2 ?? '—' },
                                            { label: 'Bairro', value: user.user_fields?.bairro ?? '—' },
                                            { label: 'Cidade', value: user.user_fields?.cidade ?? '—' },
                                            { label: 'Estado', value: user.user_fields?.estado ?? '—' },
                                            { label: 'Complemento', value: user.user_fields?.estado ?? '—' },
                                        ].map((item, idx) => (
                                            <div
                                                key={idx}
                                                className={`grid grid-cols-2 px-4 py-2 text-sm ${idx % 2 === 0 ? 'bg-white' : 'bg-[#F8F9F9]'
                                                    }`}
                                            >
                                                <span className="text-gray-500 font-medium">{item.label}</span>
                                                <span className='text-gray-700 font-medium text-xs'>{loading ? <SkeletonRow /> : item.value}</span>
                                            </div>
                                        ))}

                                        <div className="flex justify-around items-center px-4 py-2">
                                            <button
                                                title="Visualizar histórico de alterações da conta"
                                                className="text-blue-600 cursor-pointer py-2 flex-1 text-center"
                                                onClick={() => {
                                                    setSelectedUser(user);
                                                    navigate(`/history-user/${user.id}`)
                                                }}
                                            >
                                                <MdOutlineHistory className="inline-block" />
                                            </button>
                                            {trueRoles[user.id as number] !== 'admin' && trueRoles[user.id as number] !== 'agent' && (
                                                <button
                                                    title="Editar conta"
                                                    className="text-blue-600 cursor-pointer py-2 flex-1 text-center"
                                                    onClick={() => {
                                                        setSelectedUser(user);
                                                        navigate(`/edit-user/${user.id}`);
                                                    }}
                                                >
                                                    <GoPencil className="inline-block" />
                                                </button>
                                            )}
                                         
                                            <button
                                                title="Remover usuário do objeto da conta"
                                                className="text-red-500 cursor-pointer py-2 flex-1 text-center"
                                                onClick={() => openModal(user)}
                                            >
                                                <GoTrash className="inline-block" />
                                            </button>
                                        </div>
                                    </div>
                                ),
                            }}
                        />
                    ))}
                        <div className='h-4 border-b border-gray-200 mb-4'></div>
                            <>
                                <span className='text-gray-600 font-semibold text-sm'>Vincular conta ao ticket:</span>
                                <div>
                                    {availableRoles.map((role) => (
                                        <div className='w-full mt-2 display flex flex-col gap-2' key={role}>
                                            <button onClick={() => {
                                                setSelectedRole(role);
                                                navigate('/add-account');
                                            }} className='flex items-center gap-3 p-5 text-gray-700 text-sm font-semibold border border-gray-300 cursor-pointer hover:bg-blue-50 rounded w-full justify-between'>
                                                {formatRole(role.charAt(0).toUpperCase() + role.slice(1))}
                                                <IoIosAdd className='w-5 h-5 text-blue-500' />
                                            </button>
                                        </div>
                                    ))}

                                </div>
                            </>
                </div>
                    <div className='fixed bottom-0 left-0 w-full bg-white p-4 border-t border-gray-200'>
                        <Footer>
                            <div className='flex gap-3 flex-col'>
                                <Button isStretched isPrimary onClick={() => navigate('/select-account')}>Criar nova conta</Button>
                            </div>
                        </Footer>
                    </div>
            </div>
        </>
    )
}